import * as THREE from 'three';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

type Animation = {
  startDir: THREE.Vector3;
  rotQuat: THREE.Quaternion;
  progress: number;
  duration: number;
  distance: number;
};

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Smoothly rotates the camera to face a target direction using quaternion SLERP.
 * Respects OrbitControls — cancels if the user grabs the globe.
 */
export class CameraAnimator {
  private _anim: Animation | null = null;
  private _defaultAutoRotate: boolean;
  private readonly _camera: THREE.PerspectiveCamera;
  private readonly _controls: OrbitControls;

  constructor(camera: THREE.PerspectiveCamera, controls: OrbitControls) {
    this._camera = camera;
    this._controls = controls;
    this._defaultAutoRotate = controls.autoRotate;
    controls.addEventListener('start', () => this.cancel());
  }

  /** Rotate to face `targetDir` (world space) over `duration` seconds. */
  animateTo(targetDir: THREE.Vector3, duration = 1.5): void {
    const start = this._camera.position.clone().normalize();
    const end = targetDir.clone().normalize();
    if (start.dot(end) > 0.9999) return; // Already facing it

    this._controls.autoRotate = false;
    this._anim = {
      startDir: start,
      rotQuat: new THREE.Quaternion().setFromUnitVectors(start, end),
      progress: 0,
      duration,
      distance: this._camera.position.length(),
    };
  }

  /** Update the autoRotate default that is restored after animations complete. */
  setDefaultAutoRotate(value: boolean): void {
    this._defaultAutoRotate = value;
  }

  /** Cancel any in-progress animation and restore autoRotate. */
  cancel(): void {
    if (!this._anim) return;
    this._anim = null;
    this._controls.autoRotate = this._defaultAutoRotate;
  }

  /**
   * Advance the animation by `delta` seconds.
   * Returns true while an animation is running (caller should skip orbitalControls.update).
   */
  update(delta: number): boolean {
    if (!this._anim) {
      return false;
    }
    
    this._anim.progress = Math.min(1, this._anim.progress + delta / this._anim.duration);
    const t = easeInOutCubic(this._anim.progress);
    const q = new THREE.Quaternion().slerpQuaternions(new THREE.Quaternion(), this._anim.rotQuat, t);

    this._camera.position.copy(
      this._anim.startDir.clone().applyQuaternion(q).multiplyScalar(this._anim.distance),
    );
    this._camera.lookAt(0, 0, 0);

    if (this._anim.progress >= 1) {
      this._anim = null;
      this._controls.autoRotate = this._defaultAutoRotate;
      this._controls.update(); // sync internal spherical state to final camera position
    }

    return true;
  }
}
